import { useEffect } from '@frontend/react';
import type { SocketClient } from '@frontend/sockets';
import { useReducer } from 'react';
import {
  taskChatSocketInitialState,
  taskChatSocketStateMachine,
  type TaskChatSocketEvent,
} from '../taskChatSocketStateMachine/taskChatSocketStateMachine';

type UseTaskChatSocketProps = {
  enabled?: boolean;
  onInvalidateCache?: (taskId?: string) => void;
  onReceiveMessage?: () => void;
  refetch: () => void;
  socketClient: SocketClient;
  taskId: string;
};

export const useTaskChatSocket = ({
  enabled = true,
  onInvalidateCache,
  onReceiveMessage,
  refetch,
  socketClient,
  taskId,
}: UseTaskChatSocketProps) => {
  const { disconnectSocket, getSocket, initializeSocket } = socketClient;
  const [state, dispatch] = useReducer(taskChatSocketStateMachine, taskChatSocketInitialState);
  const { isGameplanGenerating, isThinking, thinkingTrace } = state;

  useEffect(() => {
    if (!enabled || !taskId) {
      dispatch({ type: 'reset' });
      disconnectSocket();
      return;
    }

    dispatch({ type: 'reset' });

    if (getSocket('task')) {
      disconnectSocket();
    }

    const socket = initializeSocket({
      channelType: 'task',
      params: { task_id: taskId },
    });

    const dispatchEvent = (event: TaskChatSocketEvent) => dispatch(event);

    const handleReceiveMessage = () => {
      refetch();
      onInvalidateCache?.(taskId);
      onReceiveMessage?.();
      dispatchEvent({ type: 'message_received' });
    };

    const handleGameplanGenerationStarted = () => dispatchEvent({ type: 'gameplan_generation_started' });
    const handleThinkingStarted = (event: string) => {
      dispatchEvent({ data: event ?? undefined, type: 'thinking_started' });
    };
    const handleThinkingCompleted = () => dispatchEvent({ type: 'thinking_completed' });

    socket.on('receive_message', handleReceiveMessage);
    socket.on('gameplan_generation_started', handleGameplanGenerationStarted);

    socket.on('thinking_started', handleThinkingStarted);
    socket.on('thinking_completed', handleThinkingCompleted);

    return () => {
      socket.off('receive_message', handleReceiveMessage);
      socket.off('gameplan_generation_started', handleGameplanGenerationStarted);

      socket.off('thinking_started', handleThinkingStarted);
      socket.off('thinking_completed', handleThinkingCompleted);

      disconnectSocket();
      dispatch({ type: 'reset' });
    };
  }, [enabled, taskId]);

  return {
    dispatchSocketEvent: dispatch,
    isGameplanGenerating,
    isThinking,
    thinkingTrace,
  };
};
